import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab,
  Autocomplete,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';

export default function CloudServiceInfoForm({ isEdit = false }) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    fetchCloudServiceProductData,
    masterData,
    validateStep
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 削除確認ダイアログの状態
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });
  // 新しく追加された要素へのスクロール用ref
  const formRefs = useRef([]);
  // 各カテゴリーのサービス選択肢を保持
  const [serviceOptions, setServiceOptions] = useState({});
  // 各カテゴリーのサービス入力値を保持
  const [serviceInputValues, setServiceInputValues] = useState({});

  // フォームの値を監視
  const cloud_services = watch('cloud_services') || [{ cloud_service_name: '', cloud_service_id: '', cloud_service_product_name: '', cloud_service_product_id: '' }];

  // 既に選択済みのカテゴリーを取得する関数
  const getSelectedCloudServices = useCallback(() => {
    return cloud_services
      .map(service => service.cloud_service_name)
      .filter(name => name && name.trim() !== '');
  }, [cloud_services]);

  // 既に選択済みのサービスを取得する関数
  const getSelectedServices = useCallback((currentIndex) => {
    const allServices = [];
    cloud_services.forEach((service, index) => {
      if (index !== currentIndex && service.cloud_service_product_name) {
        const services = service.cloud_service_product_name.split(', ').filter(f => f.trim() !== '');
        allServices.push(...services);
      }
    });
    return allServices;
  }, [cloud_services]);

  // カテゴリーの選択肢をフィルタリングする関数
  const getFilteredCloudServiceOptions = useCallback((currentIndex) => {
    const selectedServices = getSelectedCloudServices();
    const currentService = cloud_services[currentIndex]?.cloud_service_name;

    return (masterData.cloud_services || []).filter(service => {
      const serviceName = typeof service === 'string' ? service : service.name;
      return !selectedServices.includes(serviceName) || serviceName === currentService;
    });
  }, [masterData.cloud_services, getSelectedCloudServices, cloud_services]);

  // サービスの選択肢をフィルタリングする関数
  const getFilteredServiceOptions = useCallback((currentIndex) => {
    const selectedServices = getSelectedServices(currentIndex); // 他のフォームで選択済み
    const currentServices = cloud_services[currentIndex]?.cloud_service_product_name 
      ? cloud_services[currentIndex].cloud_service_product_name.split(', ').filter(f => f.trim() !== '')
      : []; // 現在のフォームで選択済み

    // 全ての選択済みサービスを結合
    const allSelectedServices = [...selectedServices, ...currentServices];

    return (serviceOptions[currentIndex] || []).filter(service => {
      const serviceName = typeof service === 'string' ? service : service.name;
      return !allSelectedServices.includes(serviceName);
    });
  }, [serviceOptions, getSelectedServices, cloud_services]);

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData('cloud_services');
  }, [fetchMasterData]);

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // cloud_servicesのバリデーション
    cloud_services.forEach((service, index) => {
      const hasCloudServiceName = service.cloud_service_name && service.cloud_service_name !== '';
      const hasProductName = service.cloud_service_product_name && service.cloud_service_product_name !== '';

      // クラウド名が入力されている場合、サービス名も必須にする
      if (hasCloudServiceName && !hasProductName) {
        setError(`cloud_services.${index}.cloud_service_product_name`, {
          type: 'required',
          message: 'サービス名は入力必須です'
        });
        hasError = true;
      }

      // サービス名が入力されている場合、クラウド名も必須にする
      if (hasProductName && !hasCloudServiceName) {
        setError(`cloud_services.${index}.cloud_service_name`, {
          type: 'required',
          message: 'クラウド名を入力してください'
        });
        hasError = true;
      }
    });

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(10, validateForm); // 11ステップ目（0ベースで10）
    }
  }, [validateStep, cloud_services]);

  // カテゴリー選択の変更ハンドラ
  const handleCloudServiceChange = async (index, selectedService) => {
    const newServices = [...cloud_services];

    if (typeof selectedService === 'string') {
      // 手動入力の場合
      newServices[index] = {
        ...newServices[index],
        cloud_service_name: selectedService,
        cloud_service_id: '',
        // カテゴリーが変更されたらサービスをクリア
        cloud_service_product_name: '',
        cloud_service_product_id: ''
      };
    } else if (selectedService && selectedService.name) {
      // オートコンプリートから選択した場合
      newServices[index] = {
        ...newServices[index],
        cloud_service_name: selectedService.name,
        cloud_service_id: selectedService.id || '',
        // カテゴリーが変更されたらサービスをクリア
        cloud_service_product_name: '',
        cloud_service_product_id: ''
      };

      // 選択したカテゴリーに基づいてサービスの選択肢を取得
      try {
        const serviceData = await fetchCloudServiceProductData(selectedService.name);
        setServiceOptions(prev => ({
          ...prev,
          [index]: serviceData
        }));
      } catch (error) {
        console.error('Error fetching service options:', error);
      }
    } else if (selectedService === null || selectedService === '') {
      // クリアされた場合
      newServices[index] = {
        ...newServices[index],
        cloud_service_name: '',
        cloud_service_id: '',
        cloud_service_product_name: '',
        cloud_service_product_id: ''
      };
      // サービスの選択肢もクリア
      setServiceOptions(prev => ({
        ...prev,
        [index]: []
      }));
    }

    setValue('cloud_services', newServices);

    // サービスの入力値もクリア
    setServiceInputValues(prev => ({
      ...prev,
      [index]: ''
    }));

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.cloud_services;
    if (fieldErrors && fieldErrors[index]) {
      if (fieldErrors[index].cloud_service_name) {
        clearErrors(`cloud_services.${index}.cloud_service_name`);
      }
      if (fieldErrors[index].cloud_service_product_name) {
        clearErrors(`cloud_services.${index}.cloud_service_product_name`);
      }
    }
  };

  // サービス選択の変更ハンドラ
  const handleServiceChange = (index, newValue) => {
    const newServices = [...cloud_services];

    // 選択されたサービスを文字列に変換
    const serviceNames = newValue.map(item =>
      typeof item === 'string' ? item : item.name || ''
    ).join(', ');

    newServices[index] = {
      ...newServices[index],
      cloud_service_product_name: serviceNames
    };
    setValue('cloud_services', newServices);

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.cloud_services;
    if (fieldErrors && fieldErrors[index] && fieldErrors[index].cloud_service_product_name) {
      clearErrors(`cloud_services.${index}.cloud_service_product_name`);
    }
  };

  // サービス入力値変更ハンドラ
  const handleServiceInputChange = (index, newInputValue) => {
    setServiceInputValues(prev => ({
      ...prev,
      [index]: newInputValue
    }));
  };

  // サービスの手動入力確定
  const handleServiceBlur = (index) => {
    const inputValue = serviceInputValues[index];
    if (inputValue && inputValue.trim() !== '') {
      const currentServices = cloud_services[index].cloud_service_product_name
        ? cloud_services[index].cloud_service_product_name.split(', ').filter(f => f.trim() !== '')
        : [];

      const trimmedInput = inputValue.trim();
      if (!currentServices.includes(trimmedInput)) {
        const newServices = [...currentServices, trimmedInput];
        const newCloudServices = [...cloud_services];
        newCloudServices[index] = {
          ...newCloudServices[index],
          cloud_service_product_name: newServices.join(', ')
        };
        setValue('cloud_services', newCloudServices);
      }

      // 入力値をクリア
      setServiceInputValues(prev => ({
        ...prev,
        [index]: ''
      }));
    }
  };

  // 要素を追加
  const handleAdd = () => {
    const newServices = [...cloud_services, { cloud_service_name: '', cloud_service_id: '', cloud_service_product_name: '', cloud_service_product_id: '' }];
    setValue('cloud_services', newServices);

    // 新しい要素にスクロール
    setTimeout(() => {
      const newIndex = newServices.length - 1;
      if (formRefs.current[newIndex]) {
        formRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 削除前の確認
  const handleDeleteConfirm = (index) => {
    const service = cloud_services[index];
    const hasContent = (service.cloud_service_name && service.cloud_service_name !== '') ||
                      (service.cloud_service_product_name && service.cloud_service_product_name !== '');

    if (hasContent) {
      setDeleteConfirmDialog({ open: true, index });
    } else {
      handleDelete(index);
    }
  };

  // 要素を削除
  const handleDelete = (index) => {
    const newServices = cloud_services.filter((_, i) => i !== index);
    setValue('cloud_services', newServices.length > 0 ? newServices : []);
    setDeleteConfirmDialog({ open: false, index: null });

    // サービスの選択肢と入力値もクリア
    setServiceOptions(prev => {
      const newOptions = { ...prev };
      delete newOptions[index];
      return newOptions;
    });
    setServiceInputValues(prev => {
      const newValues = { ...prev };
      delete newValues[index];
      return newValues;
    });

    // 削除に関連するエラーをクリア
    const cloudServiceNameErrorKey = `cloud_services.${index}.cloud_service_name`;
    const serviceNameErrorKey = `cloud_services.${index}.cloud_service_product_name`;
    clearErrors(cloudServiceNameErrorKey);
    clearErrors(serviceNameErrorKey);
  };

  // バリデーションエラーの取得
  const getFieldError = (index, fieldName) => {
    const fieldErrors = errors?.cloud_services;
    if (showValidation && fieldErrors && fieldErrors[index] && fieldErrors[index][fieldName]) {
      return fieldErrors[index][fieldName];
    }
    return null;
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && errors?.cloud_services) {
    cloud_services.forEach((_, index) => {
      const cloudServiceNameError = getFieldError(index, 'cloud_service_name');
      const serviceNameError = getFieldError(index, 'cloud_service_product_name');
      if (cloudServiceNameError) validationErrors.push(cloudServiceNameError.message);
      if (serviceNameError) validationErrors.push(serviceNameError.message);
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        クラウド
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* クラウドサービス項目の一覧 */}
      {cloud_services.length > 0 ? (
        cloud_services.map((service, index) => {
          const cloudServiceNameError = getFieldError(index, 'cloud_service_name');
          const serviceNameError = getFieldError(index, 'cloud_service_product_name');

          return (
            <Box
              key={index}
              ref={el => formRefs.current[index] = el}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
              data-error={!!(cloudServiceNameError || serviceNameError)}
            >
              {/* 削除ボタン */}
              <IconButton
                onClick={() => handleDeleteConfirm(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={3}>
                {/* カテゴリー */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>カテゴリー</Typography>
                    <Autocomplete
                      freeSolo
                      options={getFilteredCloudServiceOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={service.cloud_service_name || ''}
                      onChange={(event, newValue) => handleCloudServiceChange(index, newValue)}
                      onInputChange={(event, newInputValue) => {
                        // 手動入力時の値を即座に反映
                        if (event && event.type === 'change') {
                          handleCloudServiceChange(index, newInputValue);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="カテゴリーを選択または入力してください"
                          variant="outlined"
                          error={!!cloudServiceNameError}
                          helperText={cloudServiceNameError?.message}
                          onBlur={(event) => {
                            // フォーカスアウト時に手動入力値を確定
                            const inputValue = event.target.value;
                            if (inputValue && inputValue !== service.cloud_service_name) {
                              handleCloudServiceChange(index, inputValue);
                            }
                          }}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* サービス */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>サービス（複数選択可）</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={getFilteredServiceOptions(index)}
                      getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                      value={service.cloud_service_product_name ? service.cloud_service_product_name.split(', ').filter(f => f.trim() !== '') : []}
                      inputValue={serviceInputValues[index] || ''}
                      onChange={(event, newValue) => handleServiceChange(index, newValue)}
                      onInputChange={(event, newInputValue) => handleServiceInputChange(index, newInputValue)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, tagIndex) => (
                          <Chip
                            variant="filled"
                            label={option}
                            {...getTagProps({ index: tagIndex })}
                            key={tagIndex}
                            sx={{
                              backgroundColor: '#4F46E5',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#f0f0f0'
                                }
                              }
                            }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          placeholder="サービスを選択または入力してください"
                          variant="outlined"
                          error={!!serviceNameError}
                          helperText={serviceNameError?.message}
                          onBlur={() => handleServiceBlur(index)}
                          sx={{
                            '& input:focus': {
                              boxShadow: 'none',
                            },
                          }}
                        />
                      )}
                      sx={{
                        '& .MuiAutocomplete-tag': {
                          margin: '2px',
                        },
                        '& .MuiAutocomplete-inputRoot': {
                          minHeight: '56px',
                          padding: '8px 12px',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        // すべてのフォームが削除された場合のメッセージ
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            クラウドサービスの項目を追加してください
          </Typography>
        </Box>
      )}

      {/* 追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Fab
          color="primary"
          onClick={handleAdd}
          sx={{
            bgcolor: '#4F46E5',
            '&:hover': {
              bgcolor: '#4338CA',
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="primary"
          >
            キャンセル
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmDialog.index)}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
